import { useEffect } from "react";
import { api, getCurrentUser } from "@/lib/apiClient";
import { sendPushNotification } from "@/lib/notificationService";
import logger from '@/lib/logger';

interface KPI {
  id: string;
  user_id: string;
  metric_name: string;
  threshold_value: number;
  condition: string;
  notification_enabled: boolean;
}

interface Metrics {
  activeOrders: number;
  inProgressProcesses: number;
  activeOperators: number;
  completionRate: number;
}

export const useKPIMonitoring = (metrics: Metrics) => {
  useEffect(() => {
    const checkKPIs = async () => {
      try {
        // Get all KPIs for all supervisors
        const { data: kpis, error } = await api
          .from("supervisor_kpis")
          .select("*")
          .eq("notification_enabled", true);

        if (error) throw error;
        if (!kpis || kpis.length === 0) return;

        // Check each KPI against current metrics
        for (const kpi of kpis) {
          const currentValue = getMetricValue(metrics, kpi.metric_name);
          const shouldAlert = checkThreshold(currentValue, kpi.threshold_value, kpi.condition);

          if (shouldAlert) {
            await sendKPIAlert(kpi, currentValue);
          }
        }
      } catch (error) {
        logger.error("Error checking KPIs:", error);
      }
    };

    // Check KPIs whenever metrics change
    if (metrics) {
      checkKPIs();
    }
  }, [metrics]);
};

const getMetricValue = (metrics: Metrics, metricName: string): number => {
  const metricMap: Record<string, number> = {
    active_orders: metrics.activeOrders,
    in_progress_processes: metrics.inProgressProcesses,
    active_operators: metrics.activeOperators,
    completion_rate: metrics.completionRate,
  };
  return metricMap[metricName] || 0;
};

const checkThreshold = (currentValue: number, threshold: number, condition: string): boolean => {
  switch (condition) {
    case "greater_than":
      return currentValue > threshold;
    case "less_than":
      return currentValue < threshold;
    case "equal_to":
      return currentValue === threshold;
    default:
      return false;
  }
};

const getMetricDisplayName = (metricName: string): string => {
  const names: Record<string, string> = {
    active_orders: "Órdenes Activas",
    in_progress_processes: "Procesos en Curso",
    active_operators: "Operarios Activos",
    completion_rate: "Tasa de Completitud",
  };
  return names[metricName] || metricName;
};

const getConditionDisplayName = (condition: string): string => {
  const conditions: Record<string, string> = {
    greater_than: "mayor que",
    less_than: "menor que",
    equal_to: "igual a",
  };
  return conditions[condition] || condition;
};

const sendKPIAlert = async (kpi: KPI, currentValue: number) => {
  const metricName = getMetricDisplayName(kpi.metric_name);
  const conditionName = getConditionDisplayName(kpi.condition);

  await sendPushNotification({
    title: "🎯 Alerta de KPI Personalizado",
    body: `${metricName} (${currentValue}) es ${conditionName} ${kpi.threshold_value}`,
    data: {
      type: "kpi_alert",
      metric: kpi.metric_name,
      value: currentValue,
      threshold: kpi.threshold_value,
      condition: kpi.condition,
    },
    targetUserId: kpi.user_id,
  });
};
